import React from 'react';
import { Trash2, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { EBTWICBanner } from '../components/layout/EBTWICBanner';
import { TrustBadge } from '../components/ui/TrustBadge';
import { useCart } from '../hooks/useCart';
import { motion, AnimatePresence } from 'framer-motion';
export function CartPage() {
  const {
    items,
    updateQuantity,
    removeFromCart,
    subtotal,
    ebtTotal
  } = useCart();
  const deliveryFee = subtotal > 35 ? 0 : 5.99;
  const total = subtotal + deliveryFee;
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-261">
      <EBTWICBanner data-id="element-262" />
      <TopNav data-id="element-263" />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 pb-24 md:pb-12" data-id="element-264">
        {/* Header */}
        <div className="mb-6 md:mb-8" data-id="element-265">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4" data-id="element-266">
            <ArrowLeft className="w-5 h-5" data-id="element-267" />
            <span className="font-medium" data-id="element-268">Continue Shopping</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-269">
            Your Cart
          </h1>
        </div>

        {items.length > 0 ? <div className="grid lg:grid-cols-3 gap-6 md:gap-8" data-id="element-270">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-id="element-271">
              <AnimatePresence initial={false} data-id="element-272">
                {items.map(item => <motion.div key={item.id} layout initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100 flex gap-4 md:gap-6" data-id="element-273">
                    {/* Product Image */}
                    <div className="w-20 h-20 md:w-32 md:h-32 bg-gray-50 rounded-xl flex-shrink-0 overflow-hidden" data-id="element-274">
                      <img src={item.image} alt="" className="w-full h-full object-cover" data-id="element-275" />
                    </div>

                    {/* Product Info */}
                    <div className="flex-grow flex flex-col justify-between min-w-0" data-id="element-276">
                      <div data-id="element-277">
                        <div className="flex justify-between items-start gap-2 mb-2" data-id="element-278">
                          <div className="min-w-0" data-id="element-279">
                            <h3 className="font-bold text-gray-900 text-base md:text-lg mb-1 truncate" data-id="element-280">
                              {item.name}
                            </h3>
                            <div className="flex gap-2" data-id="element-281">
                              {item.isWIC && <TrustBadge type="WIC" size="sm" data-id="element-282" />}
                              {item.isEBT && <TrustBadge type="EBT" size="sm" data-id="element-283" />}
                            </div>
                          </div>
                          <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-red-500 p-1 md:p-2 transition-colors flex-shrink-0" aria-label="Remove item" data-id="element-284">
                            <Trash2 className="w-4 h-4 md:w-5 md:h-5" data-id="element-285" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3 md:mt-4" data-id="element-286">
                        <span className="font-bold text-[#2D5F3F] text-lg md:text-xl" data-id="element-287">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2 md:gap-4 bg-gray-50 rounded-xl p-1.5 md:p-2" data-id="element-288">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-white rounded-lg shadow-sm text-gray-600 font-bold hover:bg-gray-100 transition-colors" aria-label="Decrease quantity" data-id="element-289">
                            -
                          </button>
                          <span className="text-sm md:text-base font-semibold w-6 md:w-8 text-center" data-id="element-290">
                            {item.quantity}
                          </span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-white rounded-lg shadow-sm text-gray-600 font-bold hover:bg-gray-100 transition-colors" aria-label="Increase quantity" data-id="element-291">
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1" data-id="element-292">
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 sticky top-24" data-id="element-293">
                <h2 className="font-bold text-gray-900 text-xl md:text-2xl mb-6" data-id="element-294">
                  Order Summary
                </h2>

                <div className="space-y-4 text-sm md:text-base mb-6" data-id="element-295">
                  <div className="flex justify-between text-gray-600" data-id="element-296">
                    <span data-id="element-297">Subtotal</span>
                    <span className="font-semibold" data-id="element-298">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600" data-id="element-299">
                    <span data-id="element-300">Delivery</span>
                    {deliveryFee === 0 ? <span className="text-[#2D5F3F] font-bold" data-id="element-301">FREE</span> : <span className="font-semibold" data-id="element-302">
                        ${deliveryFee.toFixed(2)}
                      </span>}
                  </div>
                  {ebtTotal > 0 && <div className="flex justify-between text-[#2D5F3F] bg-green-50 p-3 rounded-lg" data-id="element-303">
                      <span className="font-semibold" data-id="element-304">EBT Eligible Amount</span>
                      <span className="font-bold" data-id="element-305">${ebtTotal.toFixed(2)}</span>
                    </div>}
                </div>

                <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-306">
                  <div className="flex justify-between items-end" data-id="element-307">
                    <span className="font-bold text-gray-900 text-lg" data-id="element-308">
                      Total
                    </span>
                    <span className="font-bold text-gray-900 text-2xl md:text-3xl" data-id="element-309">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button className="w-full bg-[#D97757] text-white font-bold py-3.5 md:py-4 rounded-xl shadow-lg hover:bg-[#c66545] transition-colors flex items-center justify-center gap-2 mb-4" data-id="element-310">
                  <ShieldCheck className="w-5 h-5" data-id="element-311" />
                  Secure Checkout
                </button>

                <div className="flex justify-center pt-4 border-t border-gray-100" data-id="element-312">
                  <TrustBadge type="USDA" data-id="element-313" />
                </div>
              </div>
            </div>
          </div> /* Empty Cart State */ : <div className="flex flex-col items-center justify-center py-16 md:py-20 text-center" data-id="element-314">
            <div className="w-24 h-24 md:w-32 md:h-32 bg-orange-50 rounded-full flex items-center justify-center mb-6 md:mb-8" data-id="element-315">
              <span className="text-5xl md:text-6xl" data-id="element-316">🛒</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-317">
              Your cart is empty
            </h2>
            <p className="text-gray-500 mb-8 md:mb-10 max-w-md text-base md:text-lg" data-id="element-318">
              Looks like you haven't added any groceries yet. Start shopping to
              fill your pantry!
            </p>
            <Link to="/" className="bg-[#D97757] text-white px-8 md:px-10 py-3.5 md:py-4 rounded-xl font-bold shadow-lg hover:bg-[#c66545] transition-colors text-base md:text-lg" data-id="element-319">
              Start Shopping
            </Link>
          </div>}
      </main>

      <BottomNav data-id="element-320" />
    </div>;
}